import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ReactNode, useRef } from 'react';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { DialogFooter, DialogHeader, DialogTitle } from '@/Domain/components/Shared/Dialog/Dialog';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';

interface IntegrationConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** The credential inputs / copy fields the user fills in or copies out. */
  children: ReactNode;
  /** Primary action label (e.g. "Connect to Telegram"). */
  submitLabel: ReactNode;
  submitIcon?: ReactNode;
  /**
   * Connect handler. Omit for copy-out flows (e.g. Drupal) where the user only
   * copies values out — the primary button then just closes the dialog.
   */
  onSubmit?: () => void;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  'data-testid'?: string;
}

/**
 * Reusable "dialog inside a sheet" modal, mirroring CreateTokenDialog. Render it
 * as a direct child of a SheetContent that uses `space-y-0` (so the base sheet
 * `space-y-4` doesn't bleed a margin onto the overlay/content); it positions
 * itself absolutely over the sheet panel. Escape is handled by Radix layering:
 * the first press closes this dialog, the next closes the sheet.
 */
export const IntegrationConnectDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  submitLabel,
  submitIcon,
  onSubmit,
  submitDisabled,
  isSubmitting,
  ...props
}: IntegrationConnectDialogProps) => {
  // Content spans the whole panel so it can centre the card, which means Radix never
  // sees a backdrop click as "outside" and the dialog only closed on the X. A press
  // that both starts and ends on the backdrop is a dismissal; a drag that began
  // inside the card is not.
  const backdropPressed = useRef(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />

      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        data-testid={props['data-testid']}
        onMouseDown={(event) => {
          backdropPressed.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          const dismiss = backdropPressed.current && event.target === event.currentTarget;
          backdropPressed.current = false;
          if (dismiss) onOpenChange(false);
        }}
      >
        <div className="bg-card rounded-lg p-6 max-w-2xl w-full shadow-lg">
          <form
            // Not a login form — keep password managers from offering to fill/save
            // the credentials: autoComplete for Apple Passwords/Safari + WebKit,
            // data-form-type for 1Password, data-lpignore for LastPass.
            autoComplete="off"
            data-form-type="other"
            data-lpignore="true"
            onSubmit={(e) => {
              e.preventDefault();
              if (onSubmit) onSubmit();
              else onOpenChange(false);
            }}
          >
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                  <DialogTitle>{title}</DialogTitle>
                  {description ? (
                    <p className="text-sm text-foreground-subtle">{description}</p>
                  ) : null}
                </div>
                <IconButton
                  type="button"
                  variant="secondary"
                  onClick={() => onOpenChange(false)}
                  className="shrink-0"
                  data-testid="integration-connect-dialog-close"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </DialogHeader>

            <div className="py-4 space-y-4">{children}</div>

            <DialogFooter>
              <Button
                type="submit"
                variant="dark"
                disabled={submitDisabled || isSubmitting}
                data-testid="integration-connect-dialog-submit"
              >
                {isSubmitting ? <Spinner className="size-4" /> : submitIcon}
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};

export default IntegrationConnectDialog;
